import { useCallback, useRef, useState } from 'react'
import { pdfjs } from 'react-pdf'
import { useWindowSize } from './useWindowSize'

const MAX_CACHE_SIZE = 10
const CACHE_TTL = 5 * 60 * 1000

interface CacheEntry {
  data: string
  timestamp: number
}

class LRUCacheWithTTL {
  private cache = new Map<string, CacheEntry>()
  private maxSize: number
  private ttl: number

  constructor(maxSize: number, ttl: number) {
    this.maxSize = maxSize
    this.ttl = ttl
  }

  get(key: string): string | undefined {
    const entry = this.cache.get(key)
    if (!entry) return undefined

    if (Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key)
      return undefined
    }

    this.cache.delete(key)
    this.cache.set(key, entry)
    return entry.data
  }

  set(key: string, value: string): void {
    if (this.cache.has(key)) {
      this.cache.delete(key)
    }

    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value
      if (firstKey) this.cache.delete(firstKey)
    }

    this.cache.set(key, {
      data: value,
      timestamp: Date.now(),
    })
  }

  has(key: string): boolean {
    return this.get(key) !== undefined
  }

  clear(): void {
    this.cache.clear()
  }
}

const pdfImageCache = new LRUCacheWithTTL(MAX_CACHE_SIZE, CACHE_TTL)

type UsePdfToImage = {
  loading: boolean
  convert: (pdfUrl: string) => Promise<string | undefined>
  clearCache: () => void
}

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url).toString()

export const usePdfToImage = (): UsePdfToImage => {
  const { isMobile } = useWindowSize()
  const [loading, setLoading] = useState<boolean>(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  const getOptimizedParams = useCallback(
    (isMobile: boolean) => ({
      scale: isMobile ? 0.3 : 1,
      quality: isMobile ? 0.2 : 0.8,
      format: 'image/jpeg' as const,
    }),
    [],
  )

  const convert = useCallback(
    async (pdfUrl: string): Promise<string | undefined> => {
      const cached = pdfImageCache.get(pdfUrl)
      if (cached) {
        return cached
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      abortControllerRef.current = new AbortController()
      const { signal } = abortControllerRef.current

      try {
        setLoading(true)

        const params = getOptimizedParams(isMobile)

        const loadingTask = pdfjs.getDocument({
          url: pdfUrl,
          disableAutoFetch: true,
          disableStream: true,
          disableFontFace: true,
          useSystemFonts: true,
          standardFontDataUrl: undefined,
          cMapUrl: undefined,
          verbosity: 0,
        })

        if (signal.aborted) throw new Error('Conversion aborted')

        const pdf = await loadingTask.promise

        if (signal.aborted) {
          pdf.destroy()
          throw new Error('Conversion aborted')
        }

        const page = await pdf.getPage(1)

        if (signal.aborted) {
          pdf.destroy()
          throw new Error('Conversion aborted')
        }

        const viewport = page.getViewport({ scale: params.scale })

        let canvas: HTMLCanvasElement | OffscreenCanvas
        let context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D

        if (typeof OffscreenCanvas !== 'undefined' && !isMobile) {
          canvas = new OffscreenCanvas(viewport.width, viewport.height)
          context = canvas.getContext('2d', {
            alpha: false,
            willReadFrequently: false,
            desynchronized: true,
          })!
        } else {
          canvas = document.createElement('canvas')
          canvas.width = viewport.width
          canvas.height = viewport.height
          context = canvas.getContext('2d', {
            alpha: false,
            willReadFrequently: false,
          })!
        }

        if (!context) throw new Error('Canvas context is null')

        const renderTask = page.render({
          canvasContext: context as any,
          viewport,
          annotationMode: 0,
          intent: 'display',
        })

        await renderTask.promise

        if (signal.aborted) {
          pdf.destroy()
          throw new Error('Conversion aborted')
        }

        let imageUrl: string

        if (canvas instanceof OffscreenCanvas) {
          const blob = await canvas.convertToBlob({
            type: params.format,
            quality: params.quality,
          })
          imageUrl = URL.createObjectURL(blob)
        } else {
          imageUrl = canvas.toDataURL(params.format, params.quality)
        }

        pdfImageCache.set(pdfUrl, imageUrl)

        if (canvas instanceof HTMLCanvasElement) {
          context.clearRect(0, 0, canvas.width, canvas.height)
          canvas.width = 1
          canvas.height = 1
          canvas.remove()
        }

        setTimeout(() => {
          try {
            pdf.cleanup()
            pdf.destroy()
          } catch (error) {
            console.warn('PDF cleanup error:', error)
          }
        }, 100)

        return imageUrl
      } catch (err) {
        if (err instanceof Error && err.message === 'Conversion aborted') {
          return undefined
        }
        console.error('PDF to image conversion failed', err)
        return undefined
      } finally {
        setLoading(false)
        abortControllerRef.current = null
      }
    },
    [isMobile, getOptimizedParams],
  )

  const clearCache = useCallback(() => {
    pdfImageCache.clear()
  }, [])

  return { convert, loading, clearCache }
}
